import { getConfig } from '../config';
import { LOGIN_PATH } from '../constants/routes';
import { OAuthentication } from '../lib/Authentication';
import {
  deauthorizeAccount,
  fetchRefreshToken,
  getMe,
  postLogin,
  resetPassword,
  forgotPassword,
} from '../services/AccountService';
import history from './history';
import storage from './storage';

const defaultProps = {
  loginRequest: postLogin,
  getProfileRequest: getMe,
  refreshTokenRequest: fetchRefreshToken,
  deauthorizeRequest: deauthorizeAccount,
  navigateToLogin: () => history.push({ pathname: LOGIN_PATH }),
  rolesAllowed: getConfig('AUTH_ROLES_ALLOWED'),
  forgotPasswordRequest: forgotPassword,
  resetPasswordRequest: resetPassword,
};

const Authentication = new OAuthentication({ storage, ...defaultProps });

// Management authentication must not log out the unrelated portal when refresh
// fails. The pairing UI offers intentional human sign-in instead.
Authentication.getHumanManagementToken = async () => {
  const auth = await storage.get('user');
  if (!auth) return null;
  if (!Authentication._isTokenExpired(auth.access?.token, auth.access?.expires * 1000)) {
    return { token: auth.access.token, isExpired: false };
  }
  try {
    const refreshed = await Authentication.refreshToken();
    const access = refreshed?.access;
    if (!Authentication._isTokenExpired(access?.token, access?.expires * 1000)) {
      return { token: access.token, isExpired: false };
    }
  } catch (_) { /* Human sign-in is an explicit UI action, never a global logout. */ }
  return null;
};

// A one-way digest scopes nonsecret recovery records to the existing API login.
// No Hub session is needed; access-token refresh preserves the refresh binding.
// The refresh credential itself is never returned, persisted anew, or logged.
Authentication.getPairingSessionScope = async () => {
  const auth = await storage.get('user');
  const binding = auth?.refresh?.token;
  if (!binding) return null;
  if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') {
    throw new Error('Secure browser session scoping is unavailable.');
  }
  const digest = await window.crypto.subtle.digest(
    'SHA-256', new TextEncoder().encode(binding)
  );
  return Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, '0')).join('');
};

export default Authentication;
